import { Link, useParams } from "react-router-dom";
import { ChevronRight, History } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { OrderCard } from "./order-card";
import { PaymentDetails } from "./payment-details";
import { RefundRequestSection } from "./refund-request-section";
import { useOrder, useRefreshOrder } from "./queries";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

/** /orders/:orderId — one order, with the way to the account's order history below it. */
export function OrderDetailsPage() {
  const { orderId = "" } = useParams();
  const order = useOrder(orderId);
  const refresh = useRefreshOrder(orderId);
  const toast = useToast();

  const recheck = () =>
    refresh.mutate(undefined, {
      onError: (err) => toast.danger("Could not check the payment", errorMessage(err, "Try again.")),
    });

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-8">
      {order.isLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-soft" />
      ) : order.isError || !order.data ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-elevated p-12 text-center">
          <p className="text-sm text-danger">{errorMessage(order.error, "Could not load this order.")}</p>
          <Link to="/orders" className="text-sm font-semibold text-brand hover:underline">View order history</Link>
        </div>
      ) : (
        <>
          <OrderCard order={order.data} onRecheck={recheck} rechecking={refresh.isPending} />
          <PaymentDetails orderId={orderId} />
          <RefundRequestSection order={order.data} />
        </>
      )}

      <Link
        to="/orders"
        className="flex items-center gap-4 rounded-2xl border border-line bg-elevated p-5 shadow-sm transition-colors hover:border-brand/40 hover:bg-soft/50"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
          <History className="h-5 w-5" />
        </span>
        <span className="flex-1">
          <span className="block font-semibold text-ink">Order history</span>
          <span className="block text-sm text-ink-2">View all your past orders</span>
        </span>
        <ChevronRight className="h-5 w-5 text-ink-3" />
      </Link>
    </div>
  );
}
